import { Divider, Radio, Space } from 'antd';
import { HEADER_HEIGHT } from 'helpers/constants';
import Typography from 'helpers/fonts';
import { useSmallScreen } from 'helpers/isSmallScreen';
import { formatMoney } from 'helpers/util';
import React, { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Info from 'components/info';
import RunningTotal from 'components/runningtotal/running-total';
import { useElementHeight } from 'helpers/useElementHeight';
import QuoteCard, { QuoteCardInfo, QuoteCardInfoOption } from './quote-card';
import { useAppState } from 'state/appstate/appstate';
import { useUiState } from 'state/ui-state/ui-state';
import { Article, Currency, Discount, FormatType, Option } from 'api';
import './qoute-sidebar.css';
import { useProductName } from 'helpers/useProductName';

const QuoteSidebar: React.FunctionComponent = () => {
  const isSmallScreen = useSmallScreen();
  const {
    currency,
    format,
    options,
    features,
    materials,
    setFormat,
    depth,
    width,
    styleId,
  } = useAppState((state) => ({
    materials: state.styleProduct.product?.materials,
    options: state.styleProduct.style?.options,
    features: state.styleProduct.style?.features,
    currency: state.uState?.currency! || Currency.USD,
    format: state.uState?.format,
    setFormat: state.setFormat,
    depth: state.styleProduct.style?.depths?.find(
      ({ depth: { value } }) => value === state.uState!.dimensions.depth
    )?.depth?.label,
    width: state.styleProduct.style?.depths
      .find((d) => d.depth.value === state.uState?.dimensions?.depth)
      ?.widths?.find(({ value }) => value === state.uState!.dimensions.width)
      ?.label,
    styleId: state.styleProduct.style?.id,
  }));
  const { bom } = useUiState((state) => ({
    bom: state.bom,
  }));
  const { t } = useTranslation();
  const styleName = useProductName();

  useEffect(() => {
    if (bom?.articles) {
      const articlesToShow = bom.articles.filter(
        (a) => a.type !== Article.type.BASE
      );
      setQuotes(
        articlesToShow
          .reduce(
            (res: [QuoteCardInfo, QuoteCardInfo], article) => {
              const isFeature = article.type === 'FEATURE';
              const optionsDefinitions: Array<Option> | undefined = !isFeature
                ? options
                : features;
              const optionType = optionsDefinitions?.find(
                ({ id }) => id === article.id
              )?.optionTypeId;
              const option: QuoteCardInfoOption = {
                type: article.type,
                instanceId: article.instanceId!, //instanceID can only be null for base
                name: article.title,
                value: article.price,
                isFeature,
              };
              if (optionType === 'doors' || optionType === 'windows') {
                res[0].options.push(option);
              } else {
                res[1].options.push(option);
              }
              return res;
            },
            [
              { title: t('detailedQuote.doorsWindows.title'), options: [] },
              { title: t('detailedQuote.otherOptions.title'), options: [] },
            ]
          )
          .filter(({ options }) => options.length)
      );
    }
  }, [bom?.articles, t]);

  const [quotes, setQuotes] = useState<QuoteCardInfo[] | undefined>(undefined);

  let ref: RefObject<HTMLDivElement> = useRef(null);
  const runningTotalHeight = useElementHeight(ref);
  const maxHeight = isSmallScreen ? 'auto' : HEADER_HEIGHT + runningTotalHeight;
  const basePrice = bom?.articles.find(
    (a) => a.type === Article.type.BASE
  )?.price;
  const subTotal = bom?.discounts.reduce((res, d) => res + d.price, 0);
  return (
    <div
      style={{
        background: Theme['@grey-lightest'],
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <div
        className="invisible-scroll"
        style={{
          padding: isSmallScreen ? '0 16px' : '16px 30px',
          maxHeight: `calc(${document.documentElement.style.getPropertyValue(
            '--app-height'
          )} - ${maxHeight}px)`,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography styleid={8}>
          {t('detailedQuote.title').toUpperCase()}
        </Typography>

        <Divider
          style={{
            borderColor: `${Theme['@black']}`,
            margin: 0,
          }}
        />

        <div
          style={{
            display: 'grid',
            gridGap: '8px',
            gridTemplateColumns: '75px auto',
            marginTop: '8px',
          }}
        >
          <div style={{ alignSelf: 'center' }}>
            <Typography styleid={6} style={{ marginRight: '8px' }}>
              {t('detailedQuote.format')}
            </Typography>
            <Info title="info" />
          </div>
          <Radio.Group
            value={format}
            onChange={(e) => setFormat(e.target.value)}
          >
            <Space size={[8, 8]} wrap>
              <Radio.Button value={FormatType.PRECUT}>
                {t('detailedQuote.preCut')}
              </Radio.Button>
              {/* Urban Pavilion style does not support pre-assembled */}
              {styleId!=='81' && (
                <Radio.Button value={FormatType.PREASSEMBLED}>
                  {t('detailedQuote.preAssembled')}
                </Radio.Button>
              )}
              {currency === Currency.CAD && (
                <Radio.Button value={FormatType.INSTALLED}>
                  {t('detailedQuote.installed')}
                </Radio.Button>
              )}
            </Space>
          </Radio.Group>
        </div>

        <div
          style={{
            marginTop: '1rem',
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography styleid={6}>
            <Typography styleid={8}>
              {depth} x {width?.split(' ')[0]} {styleName}
            </Typography>{' '}
            ({t('detailedQuote.basePrice')})
          </Typography>
          <Typography styleid={6}>
            {basePrice && formatMoney(basePrice)}
          </Typography>
        </div>

        {quotes &&
          quotes.map((quote) => (
            <QuoteCard key={quote.title} quoteCardInfo={quote} />
          ))}

        <div
          style={{
            width: '100%',
            display: 'flex',
            marginBottom: '8px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography styleid={6}>{t(`runningTotal.subTotal`)}</Typography>
          <Typography styleid={6}>
            {bom && formatMoney(bom?.totalPrice + (subTotal || 0))}
          </Typography>
        </div>
        {bom?.discounts.map((d: Discount) => (
          <div
            key={d.id}
            style={{
              width: '100%',
              display: 'flex',
              marginBottom: '8px',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography styleid={6}>{d.title}</Typography>
            <Typography styleid={6}>-{formatMoney(d.price!)}</Typography>
          </div>
        ))}
        <div
          style={{
            width: '100%',
            display: 'flex',
            marginBottom: '8px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography styleid={8}>{t(`runningTotal.grossTotal`)}</Typography>
          <Typography styleid={8}>
            {bom && formatMoney(bom.totalPrice)}
          </Typography>
        </div>

        <Typography
          styleid={7}
          style={{ marginTop: '8px', marginLeft: 'auto' }}
        >
          {t(`detailedQuote.note${currency}`)}
        </Typography>
      </div>

      {!isSmallScreen && (
        <div ref={ref}>
          <RunningTotal />
        </div>
      )}
    </div>
  );
};

export default QuoteSidebar;
